/**
 * Diálogo de crear/editar tarjeta con pestañas:
 *  - Datos (formulario principal con campos custom)
 *  - Tareas (CRUD de tareas asociadas)
 *  - Notas (timeline de notas)
 *  - Adjuntos (subida/listado/descarga/borrado)
 *  - Actividad (audit trail automático)
 *
 * Para tarjetas NUEVAS, las pestañas extras quedan deshabilitadas hasta guardar.
 */
import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Loader2, Plus, Trash2, CheckCircle2, Circle, Paperclip, Download, Activity, FileText, ListChecks, StickyNote, ChevronDown, Pin, Clock, Lock, Search } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../App";
import CreateTaskPanel from "./CreateTaskPanel";
import { isFieldVisible, getMissingStageFields } from "./fieldUtils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtFecha = (s) => (s ? new Date(s).toLocaleDateString("es-DO", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtTiempoEn = (s) => {
  if (!s) return "—";
  const ms = Date.now() - new Date(s).getTime();
  if (ms < 0) return "—";
  const horas = ms / 3600000;
  if (horas < 1) return `${Math.max(1, Math.round(ms / 60000))} min`;
  if (horas < 24) return `${horas.toFixed(1)} horas`;
  return `${(horas / 24).toFixed(1)} días`;
};

function Highlight({ label, value, accent }) {
  return (
    <div className={`rounded-md border px-2.5 py-1.5 ${accent ? "border-[#80BC00]/50 bg-[#80BC00]/5" : "border-slate-200 bg-slate-50"}`}>
      <p className="text-[9px] uppercase tracking-wider font-bold text-slate-500 leading-tight">{label}</p>
      <p className={`text-xs font-semibold truncate ${accent ? "text-[#5f8f00]" : "text-slate-800"}`} title={value}>{value}</p>
    </div>
  );
}

// Combobox buscable de clientes de Mensajería (para el título en "Visitas Clientes").
// Usa un panel EN LÍNEA (sin capas flotantes) para funcionar de forma robusta
// dentro del modal (Radix Dialog) en cualquier build.
function ClienteCombobox({ value, onChange }) {
  const { getAuthHeaders } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [custom, setCustom] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/mensajeria/clientes?limit=0`, getAuthHeaders())
      .then((r) => r.json())
      .then((d) => setClientes(Array.isArray(d) ? d : (d.items || [])))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [getAuthHeaders]);

  const filtered = clientes.filter((c) => (c.nombre || "").toLowerCase().includes(search.toLowerCase()));

  // Modo Personalizado: escribir el cliente manualmente
  if (custom) {
    return (
      <div className="space-y-1">
        <Input
          autoFocus
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Escriba el nombre del cliente"
          data-testid="card-title-custom-input"
        />
        <button
          type="button"
          className="text-xs text-[#80BC00] hover:underline"
          onClick={() => { setCustom(false); onChange(""); }}
          data-testid="card-title-usar-lista"
        >
          ← Elegir de la lista de clientes
        </button>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full h-9 border border-slate-300 rounded px-2 text-sm bg-white text-left flex items-center justify-between gap-2"
        data-testid="card-title-cliente-trigger"
      >
        <span className={value ? "text-slate-800 truncate" : "text-slate-400"}>{value || "Seleccione un cliente"}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-1 w-full bg-white border border-slate-200 rounded shadow-sm">
          <div className="p-2 border-b">
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar cliente..."
              className="w-full h-8 border border-slate-300 rounded px-2 text-sm"
              data-testid="card-title-cliente-search"
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {/* Cliente no cotejado: usar lo escrito como cliente nuevo/prospecto */}
            {search.trim() && !clientes.some((c) => (c.nombre || "").toLowerCase() === search.trim().toLowerCase()) && (
              <button
                type="button"
                onClick={() => { onChange(search.trim()); setOpen(false); setSearch(""); }}
                className="w-full text-left px-3 py-2 text-sm font-medium text-[#193966] hover:bg-slate-100 border-b bg-slate-50"
                data-testid="cliente-option-nuevo"
              >
                ➕ Usar "{search.trim()}" como cliente nuevo / prospecto
              </button>
            )}
            {/* Opción Personalizado siempre primero */}
            <button
              type="button"
              onClick={() => { setCustom(true); setOpen(false); setSearch(""); onChange(""); }}
              className="w-full text-left px-3 py-2 text-sm font-medium text-[#80BC00] hover:bg-slate-100 border-b"
              data-testid="cliente-option-personalizado"
            >
              ✏️ Personalizado (escribir manualmente)
            </button>
            {loading ? (
              <p className="text-xs text-slate-400 text-center py-4">Cargando clientes...</p>
            ) : filtered.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-4">Sin resultados</p>
            ) : filtered.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => { onChange(c.nombre); setOpen(false); setSearch(""); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-100 ${value === c.nombre ? "bg-[#80BC00]/10 font-medium" : ""}`}
                data-testid={`cliente-option-${c.id}`}
              >
                {c.nombre}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CardDialog({ pipeline, editing, onClose, onSaved, onSavedKeepOpen, initialTab }) {
  const { getAuthHeaders, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [activePipeline, setActivePipeline] = useState(pipeline);
  const [saving, setSaving] = useState(false);
  const [cardId, setCardId] = useState(editing?.id || null);
  const [comboKey, setComboKey] = useState(0);
  const [pendingTasks, setPendingTasks] = useState(0);
  const [form, setForm] = useState({
    title: "",
    stage_id: pipeline.stages?.[0]?.id || "",
    owner_id: editing ? "" : (user?.id || ""),
    value: 0,
    currency: "",
    expected_close_date: "",
    notas_rapidas: "",
    custom_fields: {},
  });

  // Mantener activePipeline sincronizado con el pipeline de origen (default).
  useEffect(() => { setActivePipeline(pipeline); }, [pipeline]);

  useEffect(() => {
    if (editing) {
      setCardId(editing.id);
      setForm({
        title: editing.title || "",
        stage_id: editing.stage_id || pipeline.stages?.[0]?.id || "",
        owner_id: editing.owner_id || "",
        value: editing.value || 0,
        currency: editing.currency || "",
        expected_close_date: editing.expected_close_date || "",
        notas_rapidas: editing.notas_rapidas || "",
        custom_fields: editing.custom_fields || {},
      });
    }
  }, [editing, pipeline]);

  useEffect(() => {
    fetch(`${API_URL}/api/users`, getAuthHeaders())
      .then((r) => r.json())
      .then((d) => setUsers(Array.isArray(d) ? d : (d.items || d.users || [])))
      .catch(() => {});
    // Lista de pipelines para el selector (default: el de origen).
    fetch(`${API_URL}/api/pipelines`, getAuthHeaders())
      .then((r) => r.json())
      .then((d) => setPipelines(d.items || []))
      .catch(() => {});
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!cardId) return;
    fetch(`${API_URL}/api/pipelines/cards/${cardId}/tasks`, getAuthHeaders())
      .then((r) => r.json())
      .then((d) => setPendingTasks((d.items || []).filter((t) => t.status !== "completada").length))
      .catch(() => {});
  }, [cardId, getAuthHeaders]);

  // Cambio de pipeline (solo en tarjetas NUEVAS): trae etapas/campos del
  // pipeline elegido y reinicia etapa + campos custom para que sean coherentes.
  const onPipelineChange = useCallback(async (pid) => {
    if (!pid || pid === activePipeline.id) return;
    try {
      const r = await fetch(`${API_URL}/api/pipelines/${pid}`, getAuthHeaders());
      const p = await r.json();
      const stages = (p.stages || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
      setActivePipeline({ ...p, stages });
      setForm((prev) => ({ ...prev, stage_id: stages?.[0]?.id || "", custom_fields: {} }));
    } catch { toast.error("No se pudo cargar el pipeline seleccionado"); }
  }, [activePipeline.id, getAuthHeaders]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setCustom = (k, v) => setForm((p) => ({ ...p, custom_fields: { ...p.custom_fields, [k]: v } }));

  const handleSave = async (saveAndNew = false) => {
    if (!form.title.trim()) { toast.error("El título es obligatorio"); return; }
    const visiblesReq = (activePipeline.fields || []).filter((f) => isFieldVisible(f, form.custom_fields) && (!(f.visible_in_stages || []).length || f.visible_in_stages.includes(form.stage_id)));
    for (const f of visiblesReq) {
      if (f.required && !form.custom_fields[f.name]) {
        toast.error(`El campo "${f.label}" es obligatorio`); return;
      }
    }
    const targetStage = (activePipeline.stages || []).find((s) => s.id === form.stage_id);
    const missingGate = getMissingStageFields(targetStage, activePipeline.fields, { custom_fields: form.custom_fields, expected_close_date: form.expected_close_date });
    if (missingGate.length) {
      toast.error(`La etapa "${targetStage?.name}" requiere completar: ${missingGate.map((m) => m.label).join(", ")}`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        pipeline_id: activePipeline.id, stage_id: form.stage_id,
        title: form.title.trim(), owner_id: form.owner_id || null,
        value: Number(form.value) || 0,
        currency: form.currency || activePipeline.currency || "DOP",
        expected_close_date: form.expected_close_date || "",
        custom_fields: form.custom_fields,
        notas_rapidas: form.notas_rapidas || "",
      };
      const url = cardId ? `${API_URL}/api/pipelines/cards/${cardId}` : `${API_URL}/api/pipelines/cards`;
      const method = cardId ? "PUT" : "POST";
      const r = await fetch(url, {
        method, headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error"); }
      const result = await r.json();
      if (saveAndNew && !cardId) {
        toast.success("Tarjeta creada. Podés crear la siguiente.");
        setForm((p) => ({ ...p, title: "", value: 0, currency: "", expected_close_date: "", notas_rapidas: "", custom_fields: {} }));
        setComboKey((k) => k + 1);
        onSavedKeepOpen?.();
      } else {
        if (!cardId && result.id) setCardId(result.id);
        toast.success(cardId ? "Tarjeta actualizada" : "Tarjeta creada. Ya podés agregar tareas, notas y adjuntos.");
        onSaved?.();
      }
    } catch (err) { toast.error(err.message || "Error"); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="pipeline-card-dialog">
        <DialogHeader>
          <DialogTitle>{cardId ? `Editar tarjeta · ${form.title || "(sin título)"}` : "Nueva tarjeta"}</DialogTitle>
        </DialogHeader>
        {editing && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="card-highlights">
            <Highlight label="Fecha de creación" value={fmtFecha(editing.created_at)} />
            <Highlight label="Etapa actual" value={(activePipeline.stages || []).find((s) => s.id === form.stage_id)?.name || "—"} />
            <Highlight label="Última actividad" value={fmtFecha(editing.updated_at)} />
            <Highlight label="Tiempo en etapa" value={fmtTiempoEn(editing.stage_entered_at || editing.created_at)} accent />
          </div>
        )}
        <Tabs defaultValue={initialTab && cardId ? initialTab : "datos"}>
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="datos" data-testid="card-tab-datos"><FileText className="w-3 h-3 mr-1.5" /> Datos</TabsTrigger>
            <TabsTrigger value="tareas" disabled={!cardId} data-testid="card-tab-tareas">
              <ListChecks className="w-3 h-3 mr-1.5" /> Tareas
              {pendingTasks > 0 && <Badge className="ml-1.5 bg-amber-500 hover:bg-amber-500 text-white text-[9px] h-4 px-1.5" data-testid="card-pending-tasks-badge">{pendingTasks}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="notas" disabled={!cardId} data-testid="card-tab-notas"><StickyNote className="w-3 h-3 mr-1.5" /> Notas</TabsTrigger>
            <TabsTrigger value="adjuntos" disabled={!cardId} data-testid="card-tab-adjuntos"><Paperclip className="w-3 h-3 mr-1.5" /> Adjuntos</TabsTrigger>
            <TabsTrigger value="actividad" disabled={!cardId} data-testid="card-tab-actividad"><Activity className="w-3 h-3 mr-1.5" /> Actividad</TabsTrigger>
          </TabsList>

          {/* DATOS */}
          <TabsContent value="datos" className="space-y-3 pt-3">
            <div>
              <Label className="text-xs">Título <span className="text-rose-500">*</span></Label>
              {activePipeline.titulo_cotejar_clientes === false ? (
                <Input
                  value={form.title}
                  placeholder="Escribe el nombre del negocio / cliente o prospecto"
                  data-testid="card-title-libre-input"
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((p) => {
                      const next = { ...p, title: v };
                      const clienteField = (activePipeline.fields || []).find(
                        (f) => (f.name || "").toLowerCase() === "cliente" || (f.label || "").toLowerCase() === "cliente"
                      );
                      if (clienteField) next.custom_fields = { ...p.custom_fields, [clienteField.name]: v };
                      return next;
                    });
                  }}
                />
              ) : (
              <ClienteCombobox key={comboKey} value={form.title} onChange={(v) => {
                setForm((p) => {
                  const next = { ...p, title: v };
                  const clienteField = (activePipeline.fields || []).find(
                    (f) => (f.name || "").toLowerCase() === "cliente" || (f.label || "").toLowerCase() === "cliente"
                  );
                  if (clienteField) next.custom_fields = { ...p.custom_fields, [clienteField.name]: v };
                  return next;
                });
              }} />
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {!cardId && (
                <div className="col-span-2">
                  <Label className="text-xs">Pipeline</Label>
                  <select value={activePipeline.id} onChange={(e) => onPipelineChange(e.target.value)} className="w-full h-9 border border-slate-300 rounded px-2 text-sm bg-white" data-testid="card-pipeline">
                    {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <Label className="text-xs">Etapa</Label>
                <select value={form.stage_id} onChange={(e) => setField("stage_id", e.target.value)} className="w-full h-9 border border-slate-300 rounded px-2 text-sm bg-white" data-testid="card-stage">
                  {activePipeline.stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Propietario</Label>
                <select value={form.owner_id || ""} onChange={(e) => setField("owner_id", e.target.value)} className="w-full h-9 border border-slate-300 rounded px-2 text-sm bg-white" data-testid="card-owner">
                  <option value="">— Sin asignar —</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                </select>
              </div>
              {activePipeline.hide_value_fields !== true && (
                <>
                  <div>
                    <Label className="text-xs">Valor</Label>
                    <div className="flex gap-1.5">
                      <select
                        value={form.currency || activePipeline.currency || "DOP"}
                        onChange={(e) => setField("currency", e.target.value)}
                        className="h-9 border border-slate-300 rounded px-1.5 text-sm bg-white w-[76px]"
                        data-testid="card-currency"
                      >
                        {[...new Set([activePipeline.currency || "DOP", "DOP", "USD", "EUR"])].map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                      <Input type="number" min={0} step={1} value={form.value} onChange={(e) => setField("value", e.target.value)} data-testid="card-value" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Fecha de cierre esperada</Label>
                    <Input type="date" value={form.expected_close_date || ""} onChange={(e) => setField("expected_close_date", e.target.value)} data-testid="card-close-date" />
                  </div>
                </>
              )}
            </div>
            {(activePipeline.fields || []).length > 0 && (() => {
              // Estado del campo según etapa: editable / solo lectura (etapas posteriores) / oculto
              const stageIdx = (id) => (activePipeline.stages || []).findIndex((s) => s.id === id);
              const fieldStageState = (f) => {
                const vis = f.visible_in_stages || [];
                if (!vis.length || vis.includes(form.stage_id)) return "editable";
                const cur = stageIdx(form.stage_id);
                const minVis = Math.min(...vis.map(stageIdx).filter((n) => n >= 0));
                if (cur >= 0 && Number.isFinite(minVis) && cur > minVis) return "readonly";
                return "hidden";
              };
              const visibles = activePipeline.fields
                .filter((f) => isFieldVisible(f, form.custom_fields))
                .map((f) => ({ f, state: fieldStageState(f) }))
                .filter((x) => x.state !== "hidden");
              if (!visibles.length) return null;
              return (
                <div className="border-t pt-3">
                  <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Campos del pipeline</p>
                  <div className="grid grid-cols-2 gap-3">
                    {visibles.map(({ f, state }) => (
                      <CustomFieldInput key={f.id} field={f} value={form.custom_fields[f.name]} onChange={(v) => setCustom(f.name, v)} readOnly={state === "readonly"} />
                    ))}
                  </div>
                </div>
              );
            })()}
            <div className="border-t pt-3">
              <Label className="text-xs">Notas rápidas</Label>
              <Textarea value={form.notas_rapidas || ""} onChange={(e) => setField("notas_rapidas", e.target.value)} rows={3} placeholder="Observaciones cortas, próximo paso…" />
            </div>
            <DialogFooter className="pt-2">
              <Button variant="outline" onClick={onClose}>Cerrar</Button>
              {!cardId && (
                <Button variant="outline" onClick={() => handleSave(true)} disabled={saving} className="border-[#80BC00] text-[#5f8f00] hover:bg-[#80BC00]/10" data-testid="card-save-and-new-btn">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Crear y agregar otro
                </Button>
              )}
              <Button onClick={() => handleSave(false)} disabled={saving} className="bg-[#80BC00] hover:bg-[#6ea700] text-white" data-testid="card-save-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar
              </Button>
            </DialogFooter>
          </TabsContent>

          {/* TAREAS */}
          <TabsContent value="tareas" className="pt-3">
            {cardId && <TareasTab cardId={cardId} cardTitle={form.title} users={users} onPendingChange={setPendingTasks} pipelineId={activePipeline?.id || pipeline?.id} />}
          </TabsContent>
          {/* NOTAS */}
          <TabsContent value="notas" className="pt-3">
            {cardId && <NotasTab cardId={cardId} />}
          </TabsContent>
          {/* ADJUNTOS */}
          <TabsContent value="adjuntos" className="pt-3">
            {cardId && <AdjuntosTab cardId={cardId} />}
          </TabsContent>
          {/* ACTIVIDAD */}
          <TabsContent value="actividad" className="pt-3">
            {cardId && <ActividadTab cardId={cardId} />}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// CustomFieldInput (exportado: también lo usa StageGateDialog)
// ============================================================
export function CustomFieldInput({ field, value, onChange, testIdPrefix = "card-cf-", readOnly = false }) {
  // "Grupo Ramo" se calcula automáticamente desde el Ramo: siempre solo lectura
  readOnly = readOnly || field.name === "grupo_ramo";
  // Búsqueda de opciones con debounce: no filtra en cada letra, espera 400ms
  const [optInput, setOptInput] = useState("");
  const [optQuery, setOptQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setOptQuery(optInput), 400);
    return () => clearTimeout(t);
  }, [optInput]);
  const allOpts = field.options || [];
  const showSearch = ["select", "multiselect"].includes(field.field_type) && allOpts.length > 5 && !readOnly;
  const q = optQuery.trim().toLowerCase();
  const filteredOpts = showSearch && q ? allOpts.filter((o) => String(o).toLowerCase().includes(q)) : allOpts;
  const searchBox = showSearch ? (
    <div className="relative mb-1">
      <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
      <input
        type="text"
        value={optInput}
        onChange={(e) => setOptInput(e.target.value)}
        placeholder="Buscar opción..."
        className="w-full h-7 border border-slate-300 rounded pl-7 pr-2 text-xs bg-white"
        data-testid={`${testIdPrefix}${field.name}-search`}
      />
    </div>
  ) : null;

  const label = (
    <Label className="text-xs flex items-center gap-1">
      {field.label} {field.required && !readOnly && <span className="text-rose-500">*</span>}
      {readOnly && <Lock className="w-3 h-3 text-slate-400" title={field.name === "grupo_ramo" ? "Calculado automáticamente según el Ramo" : "Solo lectura en esta etapa"} />}
      {field.name === "grupo_ramo" && <span className="text-[10px] text-slate-400">(automático según Ramo)</span>}
    </Label>
  );
  const common = { value: value || "", onChange: (e) => onChange(e.target.value), placeholder: field.placeholder, disabled: readOnly, "data-testid": `${testIdPrefix}${field.name}` };
  let input;
  switch (field.field_type) {
    case "textarea": input = <Textarea rows={2} {...common} />; break;
    case "number": case "currency": input = <Input type="number" {...common} />; break;
    case "date": input = <Input type="date" {...common} />; break;
    case "datetime": input = <Input type="datetime-local" {...common} />; break;
    case "time": input = <Input type="time" {...common} />; break;
    case "email": input = <Input type="email" {...common} />; break;
    case "phone": input = <Input type="tel" {...common} />; break;
    case "checkbox":
      input = (<label className="flex items-center gap-2 h-9 text-sm"><input type="checkbox" checked={!!value} disabled={readOnly} onChange={(e) => onChange(e.target.checked)} /> {field.placeholder || "Sí / No"}</label>);
      break;
    case "select":
      input = (
        <>
          {searchBox}
          <select {...common} className="w-full h-9 border border-slate-300 rounded px-2 text-sm bg-white">
            <option value="">— Seleccione —</option>
            {value && !filteredOpts.includes(value) && <option value={value}>{value}</option>}
            {filteredOpts.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          {showSearch && q && filteredOpts.length === 0 && <p className="text-[10px] text-slate-400 mt-0.5">Sin coincidencias para "{optQuery}"</p>}
        </>
      );
      break;
    case "multiselect":
      input = (
        <>
          {searchBox}
          <div className={`w-full border border-slate-300 rounded px-2 py-1.5 max-h-[120px] overflow-y-auto space-y-1 ${readOnly ? "bg-slate-50" : "bg-white"}`} data-testid={`${testIdPrefix}${field.name}`}>
            {allOpts.length === 0 && <p className="text-[11px] text-slate-400">Sin opciones configuradas</p>}
            {showSearch && q && filteredOpts.length === 0 && <p className="text-[11px] text-slate-400">Sin coincidencias para "{optQuery}"</p>}
            {filteredOpts.map((o) => {
              const sel = Array.isArray(value) ? value : [];
              const checked = sel.includes(o);
              return (
                <label key={o} className={`flex items-center gap-2 text-sm text-slate-700 ${readOnly ? "cursor-default" : "cursor-pointer"}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={readOnly}
                    onChange={(e) => onChange(e.target.checked ? [...sel, o] : sel.filter((v) => v !== o))}
                    className="w-4 h-4 accent-[#193966]"
                    data-testid={`${testIdPrefix}${field.name}-${o}`}
                  /> {o}
                </label>
              );
            })}
          </div>
          {showSearch && Array.isArray(value) && value.length > 0 && <p className="text-[10px] text-slate-500 mt-0.5">{value.length} seleccionada(s)</p>}
        </>
      );
      break;
    default: input = <Input type="text" {...common} />;
  }
  return (<div className={field.field_type === "textarea" ? "col-span-2" : ""}>{label}{input}{readOnly && <p className="text-[10px] text-slate-400 mt-0.5">Solo lectura en esta etapa</p>}{field.help_text && <p className="text-[10px] text-slate-400 mt-0.5">{field.help_text}</p>}</div>);
}

// ============================================================
// TAREAS TAB
// ============================================================
function TareasTab({ cardId, cardTitle, users, onPendingChange, pipelineId = "" }) {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/pipelines/cards/${cardId}/tasks`, getAuthHeaders());
      const d = await r.json();
      const items = d.items || [];
      setTasks(items);
      onPendingChange?.(items.filter((t) => t.status !== "completada").length);
    } catch {}
    setLoading(false);
  }, [cardId, getAuthHeaders, onPendingChange]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (t) => {
    const status = t.status === "completada" ? "pendiente" : "completada";
    const r = await fetch(`${API_URL}/api/pipelines/tasks/${t.id}`, {
      method: "PUT", headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
      body: JSON.stringify({ ...t, status }),
    });
    if (!r.ok) {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || "No se pudo actualizar la tarea");
      return;
    }
    load();
  };
  const del = async (t) => {
    if (!window.confirm("¿Eliminar tarea?")) return;
    await fetch(`${API_URL}/api/pipelines/tasks/${t.id}`, { method: "DELETE", headers: getAuthHeaders().headers });
    load();
  };

  const hoyStr = new Date().toLocaleDateString("en-CA");
  const pendientes = tasks.filter((t) => t.status !== "completada");
  const proximas = pendientes.filter((t) => !t.due_date || (t.due_date || "").slice(0, 10) >= hoyStr);
  const vencidas = pendientes.filter((t) => t.due_date && (t.due_date || "").slice(0, 10) < hoyStr);
  const completadas = tasks.filter((t) => t.status === "completada");

  const renderTask = (t) => {
    const ticketPendiente = t.helpdesk_ticket_numero && t.status !== "completada";
    return (
    <div key={t.id} className="flex items-center gap-2 p-2 rounded border hover:bg-slate-50" data-testid={`task-row-${t.id}`}>
      <button
        onClick={() => toggle(t)}
        className="flex-shrink-0"
        title={ticketPendiente ? `El ticket #${t.helpdesk_ticket_numero} debe cerrarse en Mesa de Ayuda; la tarea se completará automáticamente.` : ""}
        data-testid={`task-toggle-${t.id}`}
      >
        {t.status === "completada" ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : ticketPendiente ? <Lock className="w-5 h-5 text-sky-500" /> : <Circle className="w-5 h-5 text-slate-400" />}
      </button>
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${t.status === "completada" ? "line-through text-slate-400" : "text-slate-800"} truncate`}>{t.title}</p>
        <div className="flex gap-2 text-[10px] text-slate-500 items-center flex-wrap">
          {t.due_date && <span>Vence: {new Date(t.due_date).toLocaleDateString("es-DO")}</span>}
          {t.priority && t.priority !== "ninguna" && <span>· Prioridad: {t.priority}{t.sla_hours ? ` (SLA ${t.sla_hours}h)` : ""}</span>}
          {t.assigned_to_name && <span>· {t.assigned_to_name}</span>}
          {t.helpdesk_ticket_numero && (
            <button
              type="button"
              onClick={() => t.helpdesk_ticket_id && navigate(`/mesa-ayuda/tickets/${t.helpdesk_ticket_id}`)}
              title="Ir al ticket en Mesa de Ayuda"
              data-testid={`task-ticket-badge-${t.id}`}
            >
              <Badge variant="outline" className="text-[9px] border-sky-300 text-sky-700 py-0 cursor-pointer hover:bg-sky-100 hover:underline">
                Ticket #{t.helpdesk_ticket_numero}
              </Badge>
            </button>
          )}
          {t.completed_via === "mesa_de_ayuda" && <span className="text-emerald-600">· Cerrada vía Mesa de Ayuda</span>}
          {ticketPendiente && <span className="text-sky-600">· Se completa al cerrar el ticket</span>}
        </div>
      </div>
      <button onClick={() => del(t)} className="p-1 hover:bg-rose-50 rounded text-rose-500" data-testid={`task-delete-${t.id}`}><Trash2 className="w-4 h-4" /></button>
    </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={() => setPanelOpen(true)} className="bg-[#80BC00] hover:bg-[#6ea700] text-white" data-testid="task-open-panel-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Crear tarea
        </Button>
      </div>
      <CreateTaskPanel
        open={panelOpen}
        onOpenChange={setPanelOpen}
        cardId={cardId}
        cardLabel={cardTitle}
        users={users}
        pipelineId={pipelineId}
        onCreated={load}
      />
      {loading ? <Loader2 className="w-5 h-5 mx-auto animate-spin text-slate-400" /> : tasks.length === 0 ? (
        <p className="text-center text-xs text-slate-400 py-6">No hay tareas aún.</p>
      ) : (
        <div className="space-y-4">
          <div data-testid="tasks-proximas-section">
            <p className="text-xs uppercase tracking-wider font-bold text-[#5f8f00] mb-1.5 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> Próximas actividades ({proximas.length})
            </p>
            {proximas.length === 0 ? (
              <p className="text-xs text-slate-400 pl-1">Sin actividades próximas.</p>
            ) : <div className="space-y-2">{proximas.map(renderTask)}</div>}
          </div>
          {vencidas.length > 0 && (
            <div data-testid="tasks-vencidas-section">
              <p className="text-xs uppercase tracking-wider font-bold text-rose-600 mb-1.5">Vencidas ({vencidas.length})</p>
              <div className="space-y-2">{vencidas.map(renderTask)}</div>
            </div>
          )}
          {completadas.length > 0 && (
            <div data-testid="tasks-completadas-section">
              <p className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-1.5">Completadas ({completadas.length})</p>
              <div className="space-y-2">{completadas.map(renderTask)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// NOTAS TAB
// ============================================================
function NotasTab({ cardId }) {
  const { getAuthHeaders } = useAuth();
  const [notes, setNotes] = useState([]);
  const [content, setContent] = useState("");

  const load = useCallback(async () => {
    const r = await fetch(`${API_URL}/api/pipelines/cards/${cardId}/notes`, getAuthHeaders());
    const d = await r.json();
    setNotes(d.items || []);
  }, [cardId, getAuthHeaders]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!content.trim()) return;
    await fetch(`${API_URL}/api/pipelines/cards/${cardId}/notes`, {
      method: "POST", headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
      body: JSON.stringify({ content: content.trim() }),
    });
    setContent("");
    load();
  };
  const del = async (n) => {
    if (!window.confirm("¿Eliminar nota?")) return;
    await fetch(`${API_URL}/api/pipelines/notes/${n.id}`, { method: "DELETE", headers: getAuthHeaders().headers });
    load();
  };
  const togglePin = async (n) => {
    await fetch(`${API_URL}/api/pipelines/notes/${n.id}/pin`, { method: "POST", headers: getAuthHeaders().headers });
    load();
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Escribir una nota…" rows={2} className="flex-1" data-testid="note-new-content" />
        <Button onClick={add} className="bg-[#80BC00] hover:bg-[#6ea700] text-white" data-testid="note-add-btn"><Plus className="w-4 h-4" /></Button>
      </div>
      {notes.length === 0 ? (
        <p className="text-center text-xs text-slate-400 py-6">No hay notas aún.</p>
      ) : notes.map((n) => (
        <div key={n.id} className={`p-3 rounded border ${n.pinned ? "bg-[#80BC00]/5 border-[#80BC00]/50" : "bg-amber-50 border-amber-200"}`} data-testid={`note-row-${n.id}`}>
          <div className="flex justify-between items-start mb-1">
            <span className="text-xs font-bold text-slate-700 flex items-center gap-1">
              {n.pinned && <Pin className="w-3 h-3 text-[#5f8f00] fill-current" />}
              {n.author_name}
              {n.pinned && <Badge variant="outline" className="text-[9px] py-0 border-[#80BC00]/60 text-[#5f8f00]">Anclada</Badge>}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-slate-500">{new Date(n.created_at).toLocaleString("es-DO")}</span>
              <button
                onClick={() => togglePin(n)}
                className={`rounded p-0.5 ${n.pinned ? "text-[#5f8f00] hover:bg-[#80BC00]/20" : "text-slate-400 hover:bg-slate-200"}`}
                title={n.pinned ? "Desanclar nota" : "Anclar nota arriba"}
                data-testid={`note-pin-btn-${n.id}`}
              >
                <Pin className={`w-3 h-3 ${n.pinned ? "fill-current" : ""}`} />
              </button>
              <button onClick={() => del(n)} className="text-rose-500 hover:bg-rose-100 rounded p-0.5" data-testid={`note-delete-${n.id}`}><Trash2 className="w-3 h-3" /></button>
            </div>
          </div>
          <p className="text-sm text-slate-800 whitespace-pre-wrap">{n.content}</p>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// ADJUNTOS TAB
// ============================================================
function AdjuntosTab({ cardId }) {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [tipo, setTipo] = useState("sin_clasificar");

  const TIPO_LABELS = {
    sin_clasificar: "Sin clasificar",
    informe_visita: "Informe de visita",
    factura: "Factura",
    endoso: "Endoso",
  };

  const load = useCallback(async () => {
    const r = await fetch(`${API_URL}/api/pipelines/cards/${cardId}/attachments`, getAuthHeaders());
    const d = await r.json();
    setItems(d.items || []);
  }, [cardId, getAuthHeaders]);
  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("El archivo excede 10 MB"); return; }
    setUploading(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result.split(",")[1]);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const r = await fetch(`${API_URL}/api/pipelines/cards/${cardId}/attachments`, {
        method: "POST", headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ name: file.name, size: file.size, content_type: file.type, data_b64: b64, tipo }),
      });
      if (!r.ok) { const ed = await r.json(); throw new Error(ed.detail || "Error"); }
      toast.success(tipo === "informe_visita" ? "Informe de visita subido y disponible en el módulo Informes" : "Archivo subido");
      load();
    } catch (err) { toast.error(err.message || "Error al subir"); }
    setUploading(false);
    e.target.value = "";
  };

  const download = async (a) => {
    try {
      const r = await fetch(`${API_URL}/api/pipelines/attachments/${a.id}/download`, getAuthHeaders());
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = a.name;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error al descargar"); }
  };
  const del = async (a) => {
    if (!window.confirm(`¿Eliminar "${a.name}"?`)) return;
    await fetch(`${API_URL}/api/pipelines/attachments/${a.id}`, { method: "DELETE", headers: getAuthHeaders().headers });
    load();
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="flex flex-col gap-1">
          <span className="text-[11px] text-slate-500">Tipo de adjunto</span>
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            className="text-sm border rounded px-2 py-2 bg-white"
            data-testid="attach-tipo-select"
          >
            <option value="sin_clasificar">Sin clasificar</option>
            <option value="informe_visita">Informe de visita</option>
            <option value="factura">Factura</option>
            <option value="endoso">Endoso</option>
          </select>
        </div>
        <label className="block sm:mt-4">
          <input type="file" onChange={handleUpload} disabled={uploading} className="hidden" data-testid="attach-input" />
          <span className="inline-flex items-center gap-2 px-3 py-2 bg-[#80BC00] hover:bg-[#6ea700] text-white text-sm rounded cursor-pointer">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />} Subir archivo (max 10 MB)
          </span>
        </label>
      </div>
      {items.length === 0 ? (
        <p className="text-center text-xs text-slate-400 py-6">No hay adjuntos.</p>
      ) : items.map((a) => (
        <div key={a.id} className="flex items-center gap-2 p-2 border rounded hover:bg-slate-50" data-testid={`attach-row-${a.id}`}>
          <Paperclip className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">{a.name}</p>
            <p className="text-[10px] text-slate-500">{(a.size / 1024).toFixed(1)} KB · {a.uploaded_by_name} · {new Date(a.uploaded_at).toLocaleDateString("es-DO")}</p>
          </div>
          {a.tipo && a.tipo !== "sin_clasificar" && (
            <Badge variant="secondary" className="text-[10px] flex-shrink-0">{TIPO_LABELS[a.tipo] || a.tipo}</Badge>
          )}
          <button onClick={() => download(a)} className="p-1 hover:bg-slate-200 rounded"><Download className="w-4 h-4 text-slate-600" /></button>
          <button onClick={() => del(a)} className="p-1 hover:bg-rose-50 rounded text-rose-500" data-testid={`attach-delete-${a.id}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// ACTIVIDAD TAB (audit trail)
// ============================================================
function ActividadTab({ cardId }) {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    const r = await fetch(`${API_URL}/api/pipelines/cards/${cardId}/actividad`, getAuthHeaders());
    const d = await r.json();
    setItems(d.items || []);
  }, [cardId, getAuthHeaders]);
  useEffect(() => { load(); }, [load]);

  const LABELS = {
    created: "creó la tarjeta",
    updated: "actualizó la tarjeta",
    moved: "movió la tarjeta",
    deleted: "eliminó la tarjeta",
    task_created: "agregó una tarea",
    task_status_changed: "cambió el estado de una tarea",
    task_deleted: "eliminó una tarea",
    note_added: "agregó una nota",
    note_deleted: "eliminó una nota",
    attachment_added: "subió un adjunto",
    attachment_deleted: "eliminó un adjunto",
    concluido: "asignó la etiqueta CONCLUIDO",
    concluido_removido: "retiró la etiqueta CONCLUIDO",
  };

  if (items.length === 0) {
    return <p className="text-center text-xs text-slate-400 py-6">Sin actividad registrada todavía.</p>;
  }
  return (
    <div className="space-y-2">
      {items.map((a) => (
        <div key={a.id} className="flex gap-2 p-2 text-xs border-l-2 border-slate-200 pl-3" data-testid={`activity-row-${a.id}`}>
          <Activity className="w-3 h-3 text-slate-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p>
              <span className="font-bold text-slate-700">{a.user_name}</span>{" "}
              <span className="text-slate-600">{LABELS[a.action] || a.action}</span>
            </p>
            {a.action === "moved" && a.details && (
              <p className="text-[10px] text-slate-500">{a.details.from_stage} → <span className="font-semibold">{a.details.to_stage}</span>{a.details.lost_reason ? ` · Motivo: ${a.details.lost_reason}` : ""}</p>
            )}
            {a.action === "concluido_removido" && a.details?.motivo && (
              <p className="text-[10px] text-slate-500">Motivo: {a.details.motivo}</p>
            )}
            {a.action === "note_added" && a.details?.preview && (
              <p className="text-[10px] text-slate-500 italic">"{a.details.preview}"</p>
            )}
            <p className="text-[10px] text-slate-400">{new Date(a.timestamp).toLocaleString("es-DO")}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
